import {
  Box,
  Button,
  ButtonIcon,
  Icon,
  Typography,
} from "@saviynt/design-system";
import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useCorrelationFields } from "./CorrelationFieldsContext";
import CorrelationRuleModal from "./CorrelationRuleModal";
import DeleteCorrelationRuleModal from "./DeleteCorrelationRuleModal";
import {
  isUnsupported,
  type CorrelationRule,
  type DecodedRule,
} from "./correlationRuleCodec";

interface CorrelationRuleListProps {
  rules: DecodedRule[];
  onChange: (rules: DecodedRule[]) => void;
  onSaveRule?: (rule: CorrelationRule) => Promise<void>;
  onDelete?: (ruleId: string) => Promise<void>;
  isDeleting?: boolean;
}

export function CorrelationRuleList({
  rules,
  onChange,
  onSaveRule,
  onDelete,
  isDeleting,
}: CorrelationRuleListProps) {
  const intl = useIntl();
  const { userFieldOptions, accountFieldOptions } = useCorrelationFields();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<CorrelationRule | null>(null);
  const [deletingRuleId, setDeletingRuleId] = useState<string | null>(null);

  const resolveLabel = (
    options: { value: string | number; label: string }[],
    value: string,
  ): string =>
    options.find((o) => String(o.value) === value)?.label || value || "—";

  const handleDeleteRule = (id: string) => {
    setDeletingRuleId(id);
  };

  const confirmDelete = () => {
    if (!deletingRuleId) return;
    if (onDelete) {
      void onDelete(deletingRuleId).then(() => {
        onChange(rules.filter((r) => r.id !== deletingRuleId));
        setDeletingRuleId(null);
      });
    } else {
      onChange(rules.filter((r) => r.id !== deletingRuleId));
      setDeletingRuleId(null);
    }
  };

  const handleEditRule = (id: string) => {
    const rule = rules.find((r) => r.id === id);
    if (rule && !isUnsupported(rule)) {
      setEditingRule(rule);
      setIsModalOpen(true);
    }
  };

  const handleModalClose = (open: boolean) => {
    setIsModalOpen(open);
    if (!open) setEditingRule(null);
  };

  const handleModalSave = async (data: CorrelationRule) => {
    if (onSaveRule) {
      await onSaveRule(data);
    }
    const exists = rules.some((r) => r.id === data.id);
    const next: DecodedRule[] = exists
      ? rules.map((r) => (r.id === data.id ? data : r))
      : [...rules, data];
    onChange(next);
  };

  return (
    <Box className="space-y-4">
      {!!deletingRuleId && (
        <DeleteCorrelationRuleModal
          isOpen={!!deletingRuleId}
          onClose={() => setDeletingRuleId(null)}
          onConfirm={confirmDelete}
          isDeleting={!!isDeleting}
        />
      )}
      {isModalOpen && (
        <CorrelationRuleModal
          isOpen={isModalOpen}
          onClose={handleModalClose}
          isEditMode={!!editingRule}
          ruleData={editingRule}
          onSave={handleModalSave}
        />
      )}
      {rules.map((rule) => {
        if (isUnsupported(rule)) {
          return (
            <div
              key={rule.id}
              className="flex items-center gap-3 rounded-lg border px-4 py-3 w-full"
              style={{
                borderColor: "var(--color-border-secondary-subtlest, #293950)",
              }}
            >
              <Typography
                kind="body3"
                color="--color-foreground-neutral-subtle"
              >
                <FormattedMessage
                  id="we4Ob4"
                  defaultMessage="Advanced rule — not editable here"
                  description="Placeholder label for unsupported correlation rule"
                />
              </Typography>
              <div className="ml-auto flex items-center gap-1">
                <ButtonIcon
                  kind="outlinedNeutral"
                  icon={<Icon kind="Delete" size="small" />}
                  ariaLabel={intl.formatMessage({
                    id: "fakYZx",
                    defaultMessage: "Delete rule",
                    description: "Delete correlation rule button aria label",
                  })}
                  onClick={() => handleDeleteRule(rule.id)}
                />
              </div>
            </div>
          );
        }

        return (
          <div
            key={rule.id}
            className="flex flex-col gap-2 rounded-lg border px-4 py-3 w-full"
            style={{
              borderColor: "var(--color-border-secondary-subtlest, #293950)",
            }}
          >
            {rule.userFields.map((uf, ufIndex) => {
              const isFirst = ufIndex === 0;

              return (
                <div key={uf.id} className="flex items-center gap-4 flex-wrap">
                  <div className="flex items-center gap-2 shrink-0">
                    <Typography
                      kind="body4"
                      color="--color-foreground-neutral-subtle"
                    >
                      <FormattedMessage
                        id="k4dTkL"
                        defaultMessage="USERS"
                        description="Users column label"
                      />
                    </Typography>
                    <Typography
                      kind="body3"
                      color="--color-foreground-neutral-bold"
                    >
                      {resolveLabel(userFieldOptions, uf.field)}
                    </Typography>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    <Typography
                      kind="body4"
                      color="--color-foreground-neutral-subtle"
                    >
                      <FormattedMessage
                        id="RVzYc3"
                        defaultMessage="INITIALCHAR"
                        description="Initial char operator label"
                      />
                    </Typography>
                    <Typography
                      kind="body3"
                      color="--color-foreground-neutral-bold"
                    >
                      {uf.initialChar ||
                        intl.formatMessage({
                          id: "J2ygci",
                          defaultMessage: "—",
                          description:
                            "Em-dash placeholder for empty side panel field value",
                        })}
                    </Typography>
                    {isFirst && (
                      <div
                        className="flex items-center justify-center rounded-full"
                        style={{
                          width: "32px",
                          height: "32px",
                          border:
                            "1px solid var(--color-border-neutral-subtle, #293950)",
                          background:
                            "var(--color-background-neutral-subtlest, transparent)",
                        }}
                      >
                        <Icon
                          kind="Exchange"
                          size="small"
                          color="--color-foreground-neutral-medium"
                        />
                      </div>
                    )}
                  </div>

                  {isFirst && (
                    <div className="flex items-center gap-2 shrink-0">
                      <Typography
                        kind="body4"
                        color="--color-foreground-neutral-subtle"
                      >
                        <FormattedMessage
                          id="qwVkxz"
                          defaultMessage="ACCOUNT"
                          description="Account column label"
                        />
                      </Typography>
                      <Typography
                        kind="body3"
                        color="--color-foreground-neutral-bold"
                      >
                        {resolveLabel(accountFieldOptions, rule.accountField)}
                      </Typography>
                    </div>
                  )}

                  <div className="flex items-center gap-1 ml-auto">
                    {isFirst && (
                      <>
                        <ButtonIcon
                          kind="outlinedNeutral"
                          icon={<Icon kind="Edit" size="small" />}
                          ariaLabel={intl.formatMessage({
                            id: "/FVOjv",
                            defaultMessage: "Edit rule",
                            description:
                              "Edit correlation rule button aria label",
                          })}
                          onClick={() => handleEditRule(rule.id)}
                        />
                        <ButtonIcon
                          kind="outlinedNeutral"
                          icon={<Icon kind="Delete" size="small" />}
                          ariaLabel={intl.formatMessage({
                            id: "fakYZx",
                            defaultMessage: "Delete rule",
                            description:
                              "Delete correlation rule button aria label",
                          })}
                          onClick={() => handleDeleteRule(rule.id)}
                        />
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}

      <div className="flex items-center gap-2">
        <Button
          kind="outlined"
          onClick={() => {
            setEditingRule(null);
            setIsModalOpen(true);
          }}
        >
          <FormattedMessage
            id="6wDsYj"
            defaultMessage="Add New Rule"
            description="Add New Rule button label"
          />
        </Button>
      </div>
    </Box>
  );
}
